import React, { useMemo, useState } from "react";
import { Modal } from "@/components/Modal";
import { ProductImage } from "@/components/ProductImage";
import { eraBucket, CATEGORY_LABELS } from "@/lib/util";
import { Search, Heart, Trophy } from "lucide-react";

const ERAS = ["1990-1994", "1995-1999", "2000-2004", "2005-2009"];
const CATS = ["Candy", "Chocolate", "Biscuit", "Chips", "Drink", "PowderedDrink", "IceCream", "Churan", "StreetFood"];

export const CollectionPanel = ({ open, onClose, products, memories, onSelect, initialTab = "explore" }) => {
  const [tab, setTab] = useState(initialTab);
  const [q, setQ] = useState("");
  const [era, setEra] = useState("");
  const [cat, setCat] = useState("");
  const [flag, setFlag] = useState(""); // forgotten | discontinued

  const filtered = useMemo(() => {
    let list = products;
    if (tab === "memories") list = list.filter((p) => memories.includes(p.id));
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(s) ||
          (p.brand || "").toLowerCase().includes(s) ||
          (p.category || "").toLowerCase().includes(s) ||
          String(p.era_start).includes(s)
      );
    }
    if (era) list = list.filter((p) => eraBucket(p.era_start) === era);
    if (cat) list = list.filter((p) => p.category === cat);
    if (flag === "forgotten") list = list.filter((p) => p.nostalgia_score >= 4);
    if (flag === "discontinued") list = list.filter((p) => p.discontinued);
    return list;
  }, [products, memories, tab, q, era, cat, flag]);

  const unlockedCount = memories.length;
  const total = products.length;
  const complete = total > 0 && unlockedCount >= total;

  const Chip = ({ active, onClick, children, tid }) => (
    <button
      data-testid={tid}
      onClick={onClick}
      className={`rounded-full border-2 border-kirana-brown px-3 py-1 text-xs font-bold transition-colors ${
        active ? "bg-kirana-red text-white" : "bg-kirana-cream text-kirana-brown hover:bg-kirana-gold"
      }`}
    >
      {children}
    </button>
  );

  return (
    <Modal open={open} onClose={onClose} testId="collection-panel" maxWidth="max-w-4xl">
      <div className="p-5 sm:p-7">
        {/* tabs */}
        <div className="flex gap-2">
          <button
            data-testid="tab-explore"
            onClick={() => setTab("explore")}
            className={`font-signboard rounded-t-md border-[3px] border-b-0 border-kirana-brown px-4 py-1.5 text-lg font-bold ${
              tab === "explore" ? "bg-kirana-red text-white" : "bg-kirana-cream text-kirana-brown"
            }`}
          >
            Explore
          </button>
          <button
            data-testid="tab-memories"
            onClick={() => setTab("memories")}
            className={`font-signboard flex items-center gap-1.5 rounded-t-md border-[3px] border-b-0 border-kirana-brown px-4 py-1.5 text-lg font-bold ${
              tab === "memories" ? "bg-kirana-red text-white" : "bg-kirana-cream text-kirana-brown"
            }`}
          >
            <Heart className="h-4 w-4" /> My Memories
          </button>
        </div>

        <div className="rounded-b-md rounded-tr-md border-[3px] border-kirana-brown bg-card p-4">
          {/* progress */}
          <div className="mb-4">
            <div className="mb-1 flex items-center justify-between text-sm font-bold text-kirana-brown">
              <span data-testid="memory-progress">Memories Unlocked: {unlockedCount} / 200+</span>
              <span className="text-kirana-brown/60">({unlockedCount}/{total} in store)</span>
            </div>
            <div className="h-3 overflow-hidden rounded-full border-2 border-kirana-brown bg-kirana-cream">
              <div
                className="h-full bg-kirana-red transition-all"
                style={{ width: `${total ? (unlockedCount / total) * 100 : 0}%` }}
              />
            </div>
            {complete && (
              <div className="mt-2 flex items-center gap-2 rounded-md border-2 border-kirana-green bg-kirana-green/10 px-3 py-2 font-bold text-kirana-green">
                <Trophy className="h-5 w-5" /> KIRANA COMPLETE! You survived the Indian 90s. ❤️
              </div>
            )}
          </div>

          {/* search */}
          <div className="mb-3 flex items-center gap-2 rounded-md border-2 border-kirana-brown bg-kirana-cream px-3 py-1.5">
            <Search className="h-4 w-4 text-kirana-brown/60" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search product, brand, year…"
              className="w-full bg-transparent text-sm text-kirana-brown outline-none placeholder:text-kirana-brown/40"
            />
          </div>

          {/* filters */}
          <div className="mb-4 flex flex-wrap gap-1.5">
            {ERAS.map((e) => (
              <Chip key={e} tid={`filter-era-${e}`} active={era === e} onClick={() => setEra(era === e ? "" : e)}>
                {e}
              </Chip>
            ))}
            <span className="mx-1 w-px bg-kirana-border" />
            {CATS.map((c) => (
              <Chip key={c} tid={`filter-cat-${c}`} active={cat === c} onClick={() => setCat(cat === c ? "" : c)}>
                {CATEGORY_LABELS[c] || c}
              </Chip>
            ))}
            <span className="mx-1 w-px bg-kirana-border" />
            <Chip tid="filter-forgotten" active={flag === "forgotten"} onClick={() => setFlag(flag === "forgotten" ? "" : "forgotten")}>
              Forgotten
            </Chip>
            <Chip tid="filter-discontinued" active={flag === "discontinued"} onClick={() => setFlag(flag === "discontinued" ? "" : "discontinued")}>
              Discontinued
            </Chip>
          </div>

          {/* grid */}
          {filtered.length === 0 ? (
            <p className="py-10 text-center text-kirana-brown/60">
              {tab === "memories" ? "No memories yet. Go explore the store! 🚶" : "Nothing matches your search."}
            </p>
          ) : (
            <div className="grid max-h-[45vh] grid-cols-2 gap-3 overflow-y-auto scrollbar-thin pr-1 sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((p) => {
                const unlocked = memories.includes(p.id);
                return (
                  <button
                    key={p.id}
                    data-testid={`collection-item-${p.id}`}
                    onClick={() => onSelect(p)}
                    className="group relative flex flex-col overflow-hidden rounded-md border-[3px] border-kirana-brown bg-card text-left transition-transform hover:scale-[1.04] hard-shadow-sm"
                  >
                    <div className="relative h-20">
                      <ProductImage product={p} />
                      {unlocked && (
                        <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-kirana-brown bg-kirana-cream">
                          <Heart className="h-3 w-3 fill-kirana-red text-kirana-red" />
                        </span>
                      )}
                    </div>
                    <div className="p-1.5">
                      <span className="line-clamp-1 text-[11px] font-bold text-kirana-brown">{p.name}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
